'use client';

import { useState } from 'react';
import { KioskData } from '../../types';
import { COMPANY_INFO } from '../../data/mock-data';

interface ReceiptPageProps {
  data: KioskData;
  onPrevious: () => void;
  onComplete: () => void;
}

export default function ReceiptPage({ data, onPrevious, onComplete }: ReceiptPageProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    shipperName,
    palletQuantity,
    selectedUnit,
    vehicleCount,
    vehicleNumber,
    contact,
    driverName,
    paymentMethod,
  } = data;

  const unitPrice = 5000;
  const totalAmount = palletQuantity * unitPrice;

  const currentDate = new Date();
  const formattedDate = `${currentDate.getFullYear()}.${String(currentDate.getMonth() + 1).padStart(2, '0')}.${String(currentDate.getDate()).padStart(2, '0')}`;

  // 각 페이지에 표시할 내용 결정
  const getPageData = (pageNum: number) => {
    if (vehicleCount === 1) {
      return {
        showDriverInfo: true,
        showQuantity: true,
        quantityText: palletQuantity.toString(),
        typeText: '11A',
      };
    } else {
      if (pageNum === 1) {
        return {
          showDriverInfo: true,
          showQuantity: false,
          quantityText: '',
          typeText: '(1대분)선택시만',
        };
      } else {
        return {
          showDriverInfo: false,
          showQuantity: false,
          quantityText: '',
          typeText: '수량',
        };
      }
    }
  };

  const currentPageData = getPageData(currentPage);

  const handlePrint = () => {
    setIsLoading(true);
    // 시뮬레이션: 인쇄 처리
    setTimeout(() => {
      setIsLoading(false);
      alert('전표가 출력되었습니다! (데모)');
      onComplete();
    }, 1500);
  };

  // 렌더 함수 (렌더 중 컴포넌트 생성 시 매 렌더마다 리마운트되는 문제 회피)
  const renderReceiptForm = ({
    bgColor,
    category,
    pageData = currentPageData,
  }: {
    bgColor: string;
    category: string;
    pageData?: typeof currentPageData;
  }) => (
    <div className="border-2 border-black bg-white p-4 w-80">
      {/* Header */}
      <div className={`${bgColor} border border-black text-center py-2 mb-4`}>
        <h2 className="text-xl font-bold">Demo Networks</h2>
      </div>
      <div className="text-center text-sm mb-4">
        파레트 이동전표
        <br />({category})
      </div>

      <div className="border border-black mb-2 text-xs">
        <div className="px-2 py-1 text-center">NO. 2025{String(currentDate.getMonth() + 1).padStart(2, '0')}{String(currentDate.getDate()).padStart(2, '0')}00001</div>
      </div>

      <table className="w-full border-collapse border border-black text-xs">
        <tbody>
          {/* 발송일 */}
          <tr>
            <td className="border border-black bg-gray-200 text-center py-1 font-semibold" colSpan={2}>
              발송일
            </td>
            <td className="border border-black text-center py-1">{formattedDate}</td>
          </tr>

          {/* 발송처 section */}
          <tr>
            <td className="border border-black bg-gray-200 text-center font-semibold w-12" rowSpan={4}>
              <div className="flex flex-col items-center justify-center h-full leading-tight">
                <div>발</div>
                <div>송</div>
                <div>처</div>
              </div>
            </td>
            <td className="border border-black bg-gray-50 text-center px-2 py-1">회사명</td>
            <td className="border border-black text-center px-2 py-1">{COMPANY_INFO.name}</td>
          </tr>
          <tr>
            <td className="border border-black bg-gray-50 text-center px-2 py-1">화주</td>
            <td className="border border-black text-center px-2 py-1">{shipperName}</td>
          </tr>
          <tr>
            <td className="border border-black bg-gray-50 text-center px-2 py-1">담당자</td>
            <td className="border border-black text-center px-2 py-1">{COMPANY_INFO.contact_person}</td>
          </tr>
          <tr>
            <td className="border border-black bg-gray-50 text-center px-2 py-1">연락처</td>
            <td className="border border-black text-center px-2 py-1">{COMPANY_INFO.contact_phone}</td>
          </tr>

          {/* 도착일 */}
          <tr>
            <td className="border border-black bg-gray-200 text-center py-1 font-semibold" colSpan={2}>
              도착일
            </td>
            <td className="border border-black text-center py-1"></td>
          </tr>

          {/* 도착처 section */}
          <tr>
            <td className="border border-black bg-gray-200 text-center font-semibold w-12" rowSpan={2}>
              <div className="flex flex-col items-center justify-center h-full leading-tight">
                <div>도</div>
                <div>착</div>
                <div>처</div>
              </div>
            </td>
            <td className="border border-black bg-gray-50 text-center px-2 py-1">회사명</td>
            <td className="border border-black text-center px-2 py-1"></td>
          </tr>
          <tr>
            <td className="border border-black bg-gray-50 text-center px-2 py-1">담당자</td>
            <td className="border border-black text-center px-2 py-1"></td>
          </tr>

          {/* 운송사 section */}
          <tr>
            <td className="border border-black bg-gray-200 text-center font-semibold w-12" rowSpan={4}>
              <div className="flex flex-col items-center justify-center h-full leading-tight">
                <div>운</div>
                <div>송</div>
                <div>사</div>
              </div>
            </td>
            <td className="border border-black bg-gray-50 text-center px-2 py-1">회사명</td>
            <td className="border border-black text-center px-2 py-1"></td>
          </tr>
          <tr>
            <td className="border border-black bg-gray-50 text-center px-2 py-1">차량번호</td>
            <td className="border border-black text-center px-2 py-1">{pageData.showDriverInfo ? vehicleNumber : ''}</td>
          </tr>
          <tr>
            <td className="border border-black bg-gray-50 text-center px-2 py-1">담당자</td>
            <td className="border border-black text-center px-2 py-1">{pageData.showDriverInfo ? driverName : ''}</td>
          </tr>
          <tr>
            <td className="border border-black bg-gray-50 text-center px-2 py-1">연락처</td>
            <td className="border border-black text-center px-2 py-1">{pageData.showDriverInfo ? contact : ''}</td>
          </tr>

          {/* 유형/수량 section */}
          <tr>
            <td className="border border-black bg-gray-200 text-center px-2 py-1" colSpan={2}>
              유형
            </td>
            <td className="border border-black bg-gray-50 text-center px-2 py-1">수량</td>
          </tr>
          <tr>
            <td className="border border-black bg-gray-200 text-center px-2 py-1" colSpan={2}>
              {pageData.typeText}
            </td>
            <td className="border border-black text-center px-2 py-1">{pageData.quantityText}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="h-full bg-white flex flex-col justify-between py-4 px-6">
      {/* 현재 페이지 정보 표시 */}
      <div className="text-center">
        <h2 className="text-xl font-bold">
          전표 {currentPage} / {vehicleCount}
        </h2>
      </div>

      {/* Receipt Forms */}
      <div className="flex justify-center items-start">
        {renderReceiptForm({ bgColor: "bg-white", category: "도착처용" })}
        <div className="w-1 border-l-4 border-dotted border-black mx-3 self-stretch"></div>
        {renderReceiptForm({ bgColor: "bg-purple-200", category: "운송회사용" })}
        <div className="w-1 border-l-4 border-dotted border-black mx-3 self-stretch"></div>
        {renderReceiptForm({ bgColor: "bg-orange-300", category: "발송처용" })}
      </div>

      {/* Info Row */}
      <div className="flex justify-center items-start w-full">
        <div className="w-80 text-center"></div>
        <div className="mx-3 w-1"></div>
        <div className="w-80 text-center">
          <div className="text-lg font-semibold">
            <p>싸인 후 사진전송</p>
          </div>
        </div>
        <div className="mx-3 w-1"></div>
        <div className="w-80 text-left">
          <div className="text-sm">
            <p>결제방법 : {paymentMethod}</p>
            <p>수　　량 : {palletQuantity}개</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center px-6">
        <button
          onClick={onPrevious}
          className="bg-green-500 hover:bg-green-600 text-white px-10 py-5 rounded-lg text-2xl font-semibold flex items-center space-x-3 transition-all duration-300"
          style={{
            clipPath: 'polygon(20% 0%, 100% 0%, 100% 100%, 20% 100%, 0% 50%)',
          }}
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
          <span>이전</span>
        </button>

        {/* 페이지 네비게이션 */}
        {vehicleCount > 1 && (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg text-lg font-semibold ${
                currentPage === 1
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              이전 페이지
            </button>

            <span className="text-lg font-bold">
              {currentPage} / {vehicleCount}
            </span>

            <button
              onClick={() => setCurrentPage(Math.min(vehicleCount, currentPage + 1))}
              disabled={currentPage === vehicleCount}
              className={`px-4 py-2 rounded-lg text-lg font-semibold ${
                currentPage === vehicleCount
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              다음 페이지
            </button>
          </div>
        )}

        <button
          onClick={handlePrint}
          disabled={isLoading}
          className={`${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-orange-500 hover:bg-orange-600'
          } text-white px-10 py-5 rounded-lg text-2xl font-semibold transition-all duration-300`}
        >
          {isLoading ? '출력 중...' : '출력'}
        </button>
      </div>
    </div>
  );
}
